import React from 'react';
import { useTranslation } from 'react-i18next';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import { formatAssetStatus } from '../../../utils/formatters';
import { calculateEOLDate, getEOLStatus } from '../../../utils/assetUtils';
import { calculateTotalMaintenanceCost, getAssetHealthStatus } from '../../../utils/financialUtils';

const AssetTable = ({
    assets,
    sortConfig,
    onSort,
    onAssetClick,
    onOpenQrModal,
    onDeleteAsset,
    isPic,
    canDeleteAsset
}) => {
    const { t } = useTranslation();

    const getSortIcon = (key) => {
        if (sortConfig.key !== key) return 'ArrowUpDown';
        return sortConfig.direction === 'asc' ? 'ArrowUp' : 'ArrowDown';
    };

    const getStatusColor = (status) => {
        switch(status) {
            case 'In Use':
                return 'bg-green-100 text-green-800 border-green-200';
            case 'Available':
                return 'bg-yellow-100 text-yellow-800 border-yellow-200';
            case 'In Repair':
                return 'bg-orange-100 text-orange-800 border-orange-200';
            case 'Broken':
                return 'bg-red-100 text-red-800 border-red-200';
            case 'retired': 
                return 'bg-muted/50 text-foreground/90 border-border';
            case 'Written-Off':
            case 'Written Off': 
                return 'bg-red-100 text-red-800 border-red-200';
            default: 
                return 'bg-primary/20 text-blue-800 border-primary/30';
        }
    };

    return (
        <div className="overflow-auto bg-card rounded-xl shadow-sm border border-border max-h-[calc(100vh-250px)] relative">
            <table className="min-w-full divide-y divide-border/60 dark:divide-border/35">
                <thead className="bg-muted sticky top-0 z-10 shadow-sm">
                    <tr>
                        <th scope="col" className="px-6 py-4 text-left text-xs font-bold text-muted-foreground uppercase tracking-wider">{t('labels.asset_tag')}</th>
                        <th scope="col" className="px-6 py-4 text-left text-xs font-bold text-muted-foreground uppercase tracking-wider">{t('asset_list.product_details')}</th>
                        <th scope="col" className="px-6 py-4 text-left text-xs font-bold text-muted-foreground uppercase tracking-wider">
                            <button onClick={() => onSort('category')} className="flex items-center gap-2 hover:text-gray-700 transition-colors">
                                {t('labels.category')}
                                <Icon name={getSortIcon('category')} size={14} />
                            </button>
                        </th>
                        <th scope="col" className="px-6 py-4 text-left text-xs font-bold text-muted-foreground uppercase tracking-wider">
                            <button onClick={() => onSort('departments.name')} className="flex items-center gap-2 hover:text-gray-700 transition-colors">
                                {t('labels.department')}
                                <Icon name={getSortIcon('departments.name')} size={14} />
                            </button>
                        </th>
                        <th scope="col" className="px-6 py-4 text-left text-xs font-bold text-muted-foreground uppercase tracking-wider">
                            <button onClick={() => onSort('suppliers.company_name')} className="flex items-center gap-2 hover:text-gray-700 transition-colors">
                                {t('labels.supplier')}
                                <Icon name={getSortIcon('suppliers.company_name')} size={14} />
                            </button>
                        </th>
                        <th scope="col" className="px-6 py-4 text-left text-xs font-bold text-muted-foreground uppercase tracking-wider">
                            <button onClick={() => onSort('status')} className="flex items-center gap-2 hover:text-gray-700 transition-colors">
                                {t('labels.status')}
                                <Icon name={getSortIcon('status')} size={14} />
                            </button>
                        </th>
                        <th scope="col" className="px-6 py-4 text-center text-xs font-bold text-muted-foreground uppercase tracking-wider">{t('asset_list.health')}</th>
                        <th scope="col" className="px-6 py-4 text-center text-xs font-bold text-muted-foreground uppercase tracking-wider">{t('asset_list.eol_status')}</th>
                        <th scope="col" className="px-6 py-4 text-left text-xs font-bold text-muted-foreground uppercase tracking-wider">
                            <button onClick={() => onSort('locations.name')} className="flex items-center gap-2 hover:text-gray-700 transition-colors">
                                {t('labels.location')}
                                <Icon name={getSortIcon('locations.name')} size={14} />
                            </button>
                        </th>
                        <th scope="col" className="px-6 py-4 text-right text-xs font-bold text-muted-foreground uppercase tracking-wider">{t('asset_list.price')}</th>
                        <th scope="col" className="px-6 py-4 text-center text-xs font-bold text-muted-foreground uppercase tracking-wider">{t('common.actions')}</th>
                    </tr>
                </thead>
                <tbody className="bg-card divide-y divide-border/50 dark:divide-border/30">
                    {assets.map((asset) => {
                        const eolDate = calculateEOLDate(asset.purchase_date, asset.lifespan_years || (asset.lifespan_months / 12));
                        const eolStatus = getEOLStatus(eolDate);
                        
                        const totalMaint = calculateTotalMaintenanceCost(asset.maintenance);
                        const health = getAssetHealthStatus(asset.purchase_price, totalMaint);
                        
                        // Fallback logic for Department: Direct Assignment > Employee's Department > N/A
                        const activeLoan = asset.loans?.find(l => l.status === 'active');
                        const departmentName = 
                            asset.departments?.name || 
                            activeLoan?.employees?.departments?.name || 
                            activeLoan?.departments?.name ||
                            t('common.na');
                        
                        return (
                            <tr 
                                key={asset.asset_tag} 
                                className="hover:bg-muted/80 transition-colors cursor-pointer group"
                                onClick={() => onAssetClick(asset)}
                            >
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <span className="font-bold text-primary group-hover:text-blue-700">
                                        {asset.asset_tag}
                                    </span>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="flex items-center gap-2">
                                        <div className="font-bold text-foreground">{asset.product_name}</div>
                                        {health.status !== 'Healthy' && (
                                            <div title={health.status} className={health.status.includes('Lemon') ? 'text-red-500' : 'text-amber-500'}>
                                                <Icon name={health.icon} size={16} />
                                            </div>
                                        )}
                                    </div>
                                    <div className="text-[11px] font-medium text-muted-foreground/70">{asset.serial_number}</div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">{asset.category}</td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">{departmentName}</td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">{asset.suppliers?.company_name || t('common.na')}</td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="flex items-center gap-2">
                                        <span className={`px-2.5 py-1 rounded-full text-[11px] font-bold border ${getStatusColor(asset.status)}`}>
                                            {formatAssetStatus(asset.status)}
                                        </span>
                                        {eolStatus.status === 'Expired' && !['Retired', 'Written-Off', 'Written Off', 'Stolen', 'lost'].includes(asset.status) && (
                                            <div title={t('asset_list.past_lifespan')} className="text-red-500 animate-pulse">
                                                <Icon name="AlertTriangle" size={16} />
                                            </div>
                                        )}
                                    </div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-center">
                                    <div className="flex justify-center" title={health.status}>
                                        <div className={`p-1 flex items-center justify-center rounded-lg border ${health.color}`}>
                                            <Icon name={health.icon} size={14} />
                                        </div>
                                    </div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-center">
                                    <span className={`px-2.5 py-1 rounded-full text-[11px] font-bold border ${eolStatus.color}`}>
                                        {eolStatus.status}
                                    </span>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">{asset.locations?.name || t('common.unassigned')}</td>
                                <td className="px-6 py-4 whitespace-nowrap text-right font-bold text-foreground">
                                    {asset.purchase_price ? `RM ${asset.purchase_price.toLocaleString()}` : '-'}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-center">
                                    <div className="flex items-center justify-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                        <Button 
                                            variant="ghost" 
                                            size="icon" 
                                            onClick={(e) => { e.stopPropagation(); onAssetClick(asset); }} 
                                            className="h-8 w-8 text-muted-foreground/70 hover:text-blue-600 hover:bg-blue-50"
                                        >
                                            <Icon name="Eye" size={16} />
                                        </Button>
                                        <Button 
                                            variant="ghost" 
                                            size="icon" 
                                            onClick={(e) => { e.stopPropagation(); onOpenQrModal(asset); }} 
                                            className="h-8 w-8 text-muted-foreground/70 hover:text-blue-600 hover:bg-blue-50"
                                        >
                                            <Icon name="QrCode" size={16} />
                                        </Button>
                                        {canDeleteAsset && (
                                            <Button 
                                                variant="ghost" 
                                                size="icon" 
                                                onClick={(e) => { e.stopPropagation(); onDeleteAsset(asset); }} 
                                                className="h-8 w-8 text-muted-foreground/70 hover:text-red-600 hover:bg-red-50"
                                            >
                                                <Icon name="Trash" size={16} />
                                            </Button>
                                        )}
                                    </div>
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
};

export default AssetTable;
